import { useCallback, useMemo } from 'react';
import { t, Trans } from '@lingui/macro';

import Plus from '@decisiv/iconix/lib/components/Plus';
import Search from '@decisiv/iconix/lib/components/Search';

import Button from '@decisiv/ui-components/lib/components/Button';
import SearchInput from '@decisiv/ui-components/lib/components/SearchInput';
import { P } from '@decisiv/ui-components/lib/components/Typography';
import Loading from '@decisiv/ui-components/lib/components/Loading';

import * as ManagementLayout from 'components/ManagementLayout';

import { useCurrentUserContext } from 'utils/User';
import PaginationFooter from 'components/PaginationFooter';
import EmptyResults from 'components/EmptyResults';

import SearchableFilter from 'components/SearchableFilter';
import { SearchProvider } from 'contexts/SearchContext';
import useDebouncedValue from 'hooks/useDebouncedValue';
import { API_SEARCH_DEBOUNCE_DELAY_MS } from '../constants';
import { useManagedOemSegmentsQuery } from './hooks/useManagedSegmentsQuery';
import useOemSegmentsFilters from './hooks/useOemSegmentsFilters';
import { useManagementPageContext } from '../contexts/ManagementPageContext';
import { NoWrapHeaderTable } from '../components/styles';
import { generateColumns } from './columns';
import { ClearAllButton } from '../styles';
import useTableUrlState from '../hooks/useTableUrlState';

export type ManagedOemSegmentType = ExtractFromArray<
  ManagedOemSegmentsQuery['managedOemSegments']['collection']
>;

const OemSegments = () => {
  const { isSuperUser } = useCurrentUserContext();

  const {
    isFiltersDirty,
    pageParams,
    handleChangeOemSegmentsFilter,
    handleClearFilters,
  } = useManagementPageContext();

  const { page, setPage, apiSort, defaultSort, onSort } =
    useTableUrlState<OemSegmentsSortColumns>('management-oem-segments');

  const [textValue, setTextValue, debouncedTextValue] = useDebouncedValue(
    '',
    API_SEARCH_DEBOUNCE_DELAY_MS,
  );

  const handleOnTextChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setTextValue(e.target.value);
    },
    [setTextValue],
  );

  const { data, loading } = useManagedOemSegmentsQuery({
    variables: {
      sort: apiSort,
      page,
      filters: {
        ids: pageParams.oemSegmentIds,
        text: debouncedTextValue.length > 2 ? debouncedTextValue : undefined,
      },
    },
  });

  const oemSegmentsFilterProps = useOemSegmentsFilters({
    value: pageParams.oemSegmentIds,
    onSubmit: handleChangeOemSegmentsFilter,
  });

  const columns = useMemo(() => generateColumns({ onSort }), [onSort]);

  const tableData = data?.managedOemSegments.collection ?? [];
  const pagination = data?.managedOemSegments.pagination;

  return (
    <ManagementLayout.TabBody.Container>
      <ManagementLayout.TabBody.Header.Container>
        <ManagementLayout.TabBody.Header.Left>
          {isSuperUser && (
            <Button
              disabled
              text={t`Create OEM Segment`}
              icon={Plus}
              size="small"
            />
          )}
        </ManagementLayout.TabBody.Header.Left>
        <ManagementLayout.TabBody.Header.Right>
          <P size="small" marginRight={1}>
            <Trans>Search Segments by Name, Segment ID, Segment Key:</Trans>
          </P>
          <SearchInput
            value={textValue}
            onChange={handleOnTextChange}
            hideLabel
            aria-label={t`Search Segments by Name, Segment ID, Segment Key`}
            name="searchBySegment"
            size="small"
          />
        </ManagementLayout.TabBody.Header.Right>
      </ManagementLayout.TabBody.Header.Container>
      <ManagementLayout.TabBody.Content.Container>
        <ManagementLayout.TabBody.Content.Filters>
          <SearchableFilter
            popoverLabel={t`OEM Segments`}
            placement="bottom-start"
            {...oemSegmentsFilterProps}
          />
          {isFiltersDirty && (
            <ClearAllButton
              variant="ghost"
              onClick={handleClearFilters}
              text={t`Clear All`}
              size="small"
            />
          )}
        </ManagementLayout.TabBody.Content.Filters>
        {loading && (
          <Loading
            flex={1}
            justifyContent="center"
            size="large"
            title={t`Loading...`}
          />
        )}
        {!loading && tableData.length === 0 && (
          <EmptyResults
            title={t`No Matches for "${debouncedTextValue}"`}
            subtitle={t`Check that all words are spelled correctly or search by a different value.`}
            icon={Search}
          />
        )}
        {!loading && tableData.length > 0 && (
          <SearchProvider searchString={debouncedTextValue}>
            <NoWrapHeaderTable
              height="100%"
              scroll={['x', 'y']}
              data={tableData}
              columns={columns}
              onSort={onSort}
              defaultSort={defaultSort}
              footer={() => (
                <PaginationFooter
                  // @ts-expect-error - JS file
                  title={t`Oem Segments`}
                  setCurrentPage={setPage}
                  totalOnPage={tableData.length}
                  page={pagination?.page}
                  perPage={pagination?.perPage}
                  totalEntries={pagination?.totalEntries}
                  totalPages={pagination?.totalPages}
                />
              )}
            />
          </SearchProvider>
        )}
      </ManagementLayout.TabBody.Content.Container>
    </ManagementLayout.TabBody.Container>
  );
};

export default OemSegments;
